import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Settings, PackageSearch, Save, Building2, Loader2, FileText, Shield, Plus, X, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminStoreSettings() {
  const [settings, setSettings] = useState({
    backorders_enabled: false,
  });
  const [companyDetails, setCompanyDetails] = useState({
    company_name: 'Tech Distribution Pty Ltd',
    abn: '12 345 678 901',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    phone: '',
    email: 'sales@techdistribution.com.au',
    website: 'www.techdistribution.com.au',
    bank_name: '',
    bank_bsb: '',
    bank_account_number: '',
    bank_account_name: '',
  });
  const [paymentTermsConfig, setPaymentTermsConfig] = useState({
    cash_terms_text: 'Payment required before dispatch',
    account_terms_text: 'Payment is due within {days} days of invoice date',
    account_terms_days: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [maintenanceConfig, setMaintenanceConfig] = useState({
    enabled: false,
    message: 'We are currently performing scheduled maintenance. Please check back soon.',
    allowed_ips: [],
  });
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [yourIp, setYourIp] = useState('');
  const [newIp, setNewIp] = useState('');

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  };

  useEffect(() => {
    fetchSettings();
    fetchCompanyDetails();
    fetchPaymentTermsConfig();
    fetchMaintenanceConfig();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/store-settings`, authHeaders);
      setSettings(res.data.settings);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/company-details`, authHeaders);
      if (res.data.company) {
        setCompanyDetails(res.data.company);
      }
    } catch (err) {
      // Company details may not exist yet, that's okay
      console.log('No company details found, using defaults');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/store-settings`, { settings }, authHeaders);
      toast.success('Store settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveCompanyDetails = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API}/api/admin/company-details`, companyDetails, authHeaders);
      toast.success('Company details saved');
    } catch (err) {
      toast.error('Failed to save company details');
    } finally {
      setSavingCompany(false);
    }
  };

  const fetchPaymentTermsConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/payment-terms-config`, authHeaders);
      if (res.data.config) {
        setPaymentTermsConfig(res.data.config);
      }
    } catch (err) {
      console.log('No payment terms config found, using defaults');
    }
  };

  const savePaymentTermsConfig = async () => {
    setSavingTerms(true);
    try {
      await axios.put(`${API}/api/admin/payment-terms-config`, paymentTermsConfig, authHeaders);
      toast.success('Payment terms saved');
    } catch (err) {
      toast.error('Failed to save payment terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const fetchMaintenanceConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/maintenance`, authHeaders);
      if (res.data.config) setMaintenanceConfig(res.data.config);
      if (res.data.your_ip) setYourIp(res.data.your_ip);
    } catch (err) {
      console.log('No maintenance config found');
    }
  };

  const saveMaintenanceConfig = async () => {
    setSavingMaintenance(true);
    try {
      await axios.put(`${API}/api/admin/maintenance`, maintenanceConfig, authHeaders);
      toast.success(maintenanceConfig.enabled ? 'Maintenance mode ENABLED' : 'Maintenance mode disabled');
    } catch (err) {
      toast.error('Failed to save maintenance config');
    } finally {
      setSavingMaintenance(false);
    }
  };

  const addIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    if (maintenanceConfig.allowed_ips.includes(ip)) {
      toast.error('IP already in whitelist');
      return;
    }
    setMaintenanceConfig({ ...maintenanceConfig, allowed_ips: [...maintenanceConfig.allowed_ips, ip] });
    setNewIp('');
  };

  const removeIp = (ip) => {
    setMaintenanceConfig({ ...maintenanceConfig, allowed_ips: maintenanceConfig.allowed_ips.filter(i => i !== ip) });
  };

  const addMyIp = () => {
    if (yourIp && !maintenanceConfig.allowed_ips.includes(yourIp)) {
      setMaintenanceConfig({ ...maintenanceConfig, allowed_ips: [...maintenanceConfig.allowed_ips, yourIp] });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="store-settings-page">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight flex items-center gap-3">
            <Settings className="h-8 w-8 text-[#78BE20]" />
            Store Settings
          </h1>
          <p className="text-zinc-400 mt-1">Configure company details and store features</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            <div className="h-32 bg-zinc-800 rounded animate-pulse" />
            <div className="h-64 bg-zinc-800 rounded animate-pulse" />
          </div>
        ) : (
          <>
            {/* Maintenance Mode */}
            <Card className={`border ${maintenanceConfig.enabled ? 'bg-amber-950/30 border-amber-600/50' : 'bg-[#18181b] border-zinc-800'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      {maintenanceConfig.enabled ? <AlertTriangle className="h-5 w-5 text-amber-400" /> : <Shield className="h-5 w-5 text-[#78BE20]" />}
                      Maintenance Mode
                      {maintenanceConfig.enabled && <span className="text-xs bg-amber-500 text-black px-2 py-0.5 rounded-full font-bold ml-2">ACTIVE</span>}
                    </CardTitle>
                    <CardDescription>
                      When enabled, customers see a maintenance page. Admin panel and whitelisted IPs can still access the site.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={saveMaintenanceConfig}
                    disabled={savingMaintenance}
                    className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                  >
                    {savingMaintenance ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {savingMaintenance ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg">
                  <div>
                    <Label className="text-white text-base">Enable Maintenance Mode</Label>
                    <p className="text-xs text-zinc-500 mt-0.5">Customer-facing pages will be blocked</p>
                  </div>
                  <Switch
                    checked={maintenanceConfig.enabled}
                    onCheckedChange={(v) => setMaintenanceConfig({ ...maintenanceConfig, enabled: v })}
                    data-testid="maintenance-toggle"
                  />
                </div>

                <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                  <div>
                    <Label className="text-zinc-300">Maintenance Message</Label>
                    <Textarea
                      value={maintenanceConfig.message}
                      onChange={(e) => setMaintenanceConfig({ ...maintenanceConfig, message: e.target.value })}
                      className="mt-1 bg-zinc-800 border-zinc-700"
                      rows={2}
                      placeholder="We are currently performing scheduled maintenance..."
                      data-testid="maintenance-message"
                    />
                  </div>
                </div>

                <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-zinc-300">IP Whitelist</Label>
                      <p className="text-xs text-zinc-500">These IPs can bypass maintenance mode and view the customer-facing site</p>
                    </div>
                    {yourIp && (
                      <Button variant="outline" size="sm" className="border-zinc-700 text-xs" onClick={addMyIp} data-testid="add-my-ip-btn">
                        <Plus className="h-3 w-3 mr-1" /> Add My IP ({yourIp})
                      </Button>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="e.g. 203.45.67.89"
                      onKeyDown={(e) => e.key === 'Enter' && addIp()}
                      data-testid="new-ip-input"
                    />
                    <Button onClick={addIp} className="bg-zinc-700 hover:bg-zinc-600" data-testid="add-ip-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {maintenanceConfig.allowed_ips.length > 0 ? (
                    <div className="space-y-1">
                      {maintenanceConfig.allowed_ips.map((ip, i) => (
                        <div key={i} className="flex items-center justify-between bg-zinc-800 px-3 py-2 rounded">
                          <code className="text-sm text-zinc-300 font-mono">{ip}</code>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400" onClick={() => removeIp(ip)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-600 italic">No IPs whitelisted. Add your IP before enabling maintenance mode.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Company Details Section */}
            <Card className="bg-[#18181b] border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-[#78BE20]" />
                      Company Details
                    </CardTitle>
                    <CardDescription>
                      These details appear on invoices, purchase orders, estimates, and other documents
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={saveCompanyDetails} 
                    disabled={savingCompany}
                    className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                  >
                    {savingCompany ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {savingCompany ? 'Saving...' : 'Save Company Details'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Company Name</Label>
                    <Input
                      value={companyDetails.company_name}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, company_name: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="Tech Distribution Pty Ltd"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">ABN</Label>
                    <Input
                      value={companyDetails.abn}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, abn: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="12 345 678 901"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300">Address Line 1</Label>
                  <Input
                    value={companyDetails.address_line1}
                    onChange={(e) => setCompanyDetails({ ...companyDetails, address_line1: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700"
                    placeholder="123 Business Street"
                  />
                </div>

                <div>
                  <Label className="text-zinc-300">Address Line 2 (Optional)</Label>
                  <Input
                    value={companyDetails.address_line2}
                    onChange={(e) => setCompanyDetails({ ...companyDetails, address_line2: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700"
                    placeholder="Unit 1, Building A"
                  />
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-zinc-300">City / Suburb</Label>
                    <Input
                      value={companyDetails.city}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="Sydney"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">State</Label>
                    <Input
                      value={companyDetails.state}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, state: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="NSW"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Postcode</Label>
                    <Input
                      value={companyDetails.postcode}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, postcode: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="2000"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Country</Label>
                    <Input
                      value={companyDetails.country}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, country: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="Australia"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-300">Phone</Label>
                    <Input
                      value={companyDetails.phone}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, phone: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="(02) 1234 5678"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Email</Label>
                    <Input
                      value={companyDetails.email}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, email: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="sales@company.com"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Website</Label>
                    <Input
                      value={companyDetails.website}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, website: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700"
                      placeholder="www.company.com.au"
                    />
                  </div>
                </div>

                {/* Bank Details Section */}
                <div className="border-t border-zinc-700 pt-4 mt-4">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#78BE20]" />
                    Bank Details (for invoices)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">Bank Name</Label>
                      <Input
                        value={companyDetails.bank_name}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, bank_name: e.target.value })}
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        placeholder="Commonwealth Bank"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Account Name</Label>
                      <Input
                        value={companyDetails.bank_account_name}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, bank_account_name: e.target.value })}
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        placeholder="Tech Distribution Pty Ltd"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-zinc-300">BSB</Label>
                      <Input
                        value={companyDetails.bank_bsb}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, bank_bsb: e.target.value })}
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        placeholder="062-000"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Account Number</Label>
                      <Input
                        value={companyDetails.bank_account_number}
                        onChange={(e) => setCompanyDetails({ ...companyDetails, bank_account_number: e.target.value })}
                        className="mt-1 bg-zinc-900 border-zinc-700"
                        placeholder="1234 5678"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Terms Configuration */}
            <Card className="bg-[#18181b] border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#78BE20]" />
                      Payment Terms
                    </CardTitle>
                    <CardDescription>
                      Configure default payment terms shown on invoices based on customer account type
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={savePaymentTermsConfig} 
                    disabled={savingTerms}
                    className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                  >
                    {savingTerms ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {savingTerms ? 'Saving...' : 'Save Payment Terms'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-zinc-900 rounded-lg space-y-4">
                  <div>
                    <Label className="text-zinc-300">Cash Customer Terms</Label>
                    <p className="text-xs text-zinc-500 mb-1">Shown on invoices for customers with account type "Cash"</p>
                    <Input
                      value={paymentTermsConfig.cash_terms_text}
                      onChange={(e) => setPaymentTermsConfig({ ...paymentTermsConfig, cash_terms_text: e.target.value })}
                      className="mt-1 bg-zinc-800 border-zinc-700"
                      placeholder="Payment required before dispatch"
                      data-testid="cash-terms-input"
                    />
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <Label className="text-zinc-300">Account Customer Terms</Label>
                    <p className="text-xs text-zinc-500 mb-1">Shown on invoices for customers with account type "Account". Use {'{days}'} as placeholder for days.</p>
                    <Input
                      value={paymentTermsConfig.account_terms_text}
                      onChange={(e) => setPaymentTermsConfig({ ...paymentTermsConfig, account_terms_text: e.target.value })}
                      className="mt-1 bg-zinc-800 border-zinc-700"
                      placeholder="Payment is due within {days} days of invoice date"
                      data-testid="account-terms-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Account Payment Days</Label>
                    <p className="text-xs text-zinc-500 mb-1">Number of days for account customer payment terms</p>
                    <Input
                      type="number"
                      value={paymentTermsConfig.account_terms_days}
                      onChange={(e) => setPaymentTermsConfig({ ...paymentTermsConfig, account_terms_days: parseInt(e.target.value) || 30 })}
                      className="mt-1 bg-zinc-800 border-zinc-700 w-32"
                      min="1"
                      data-testid="account-days-input"
                    />
                  </div>
                </div>
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                  <p className="text-sm text-blue-400">
                    <strong>Preview:</strong> Cash customers will see "<span className="text-white">{paymentTermsConfig.cash_terms_text}</span>" and 
                    Account customers will see "<span className="text-white">{paymentTermsConfig.account_terms_text.replace('{days}', String(paymentTermsConfig.account_terms_days))}</span>" on their invoices.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Backorder Settings */}
            <Card className="bg-[#18181b] border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <PackageSearch className="h-5 w-5 text-yellow-500" />
                      Backorder System
                    </CardTitle>
                    <CardDescription>
                      Allow customers to order products that are out of stock
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={saveSettings} 
                    disabled={saving}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Enable Backorders</p>
                    <p className="text-sm text-zinc-400">
                      When enabled, customers can order products beyond available stock (for products with backorders allowed)
                    </p>
                  </div>
                  <Switch
                    checked={settings.backorders_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, backorders_enabled: checked })}
                    data-testid="backorders-toggle"
                  />
                </div>
                
                {!settings.backorders_enabled && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                    <p className="text-sm text-yellow-500">
                      Backorders are currently <strong>disabled</strong>. Customers can only order up to available stock.
                    </p>
                  </div>
                )}
                
                {settings.backorders_enabled && (
                  <div className="p-3 bg-[#78BE20]/10 border border-[#78BE20]/30 rounded-md">
                    <p className="text-sm text-[#78BE20]">
                      Backorders are <strong>enabled</strong>. Products with "Allow Backorder" checked can be ordered beyond stock.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
